import {
  IQuotePaymentTermCode,
  QuotePaymentTermCode
} from '../quote-payment-term-code';
import { QuoteOrganizationEnum } from '../quote/quote.types';
import {
  IServiceComputationFieldError,
  IServicePriceComputation,
  ServiceComputationFieldError,
  ServicePriceComputation
} from '../quoteservicecomputation';
import {
  IQuoteFreightTemplate,
  IQuoteFreightTemplateData,
  IQuotePaymentTermTemplate,
  IQuotePaymentTermTemplateData,
  IQuotePaymentTermTemplateFieldError,
  IQuotePaymentTermTemplateFieldErrorData,
  IQuoteServiceTemplate,
  IQuoteServiceTemplateData,
  IQuoteServiceTemplateFieldError,
  IQuoteServiceTemplateFieldErrorData,
  IQuoteTemplateData,
  IQuoteTemplateFieldError,
  IQuoteTemplateFieldErrorData,
  IQuoteTermTemplate,
  IQuoteTermTemplateData,
  IQuoteTermTemplateFieldError,
  IQuoteTermTemplateFieldErrorData,
  QuoteTemplateTranslationLanguage
} from './quotetemplate.types';

export abstract class AbstractQuoteTemplate<T extends IQuoteTemplateData> {
  readonly id?: number;
  readonly title: string = '';
  readonly description: string = '';
  readonly order?: number;
  readonly note: string = '';
  language?: QuoteTemplateTranslationLanguage;

  constructor(data?: T) {
    if (data) {
      if (data.id) {
        this.id = data.id;
      }

      this.title = data.title ?? '';
      this.description = data.description ?? '';

      if (data.order) {
        this.order = data.order;
      }

      if (data.language) {
        this.language = data.language;
      }

      if (data.note) {
        this.note = data.note;
      }
    }
  }

  abstract validate(): boolean;
}

export class QuoteServiceTemplate
  extends AbstractQuoteTemplate<IQuoteServiceTemplateData>
  implements IQuoteServiceTemplate {
  price_computations: IServicePriceComputation[] = [];
  days_computations: IServicePriceComputation[] = [];

  constructor(data?: IQuoteServiceTemplateData) {
    super(data);
    if (data) {
      if (data.price_computations) {
        this.price_computations = data.price_computations.map(
          computation => new ServicePriceComputation(computation)
        );
      }
      if (data.days_computations) {
        this.days_computations = data.days_computations.map(
          computation => new ServicePriceComputation(computation)
        );
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class QuoteFreightTemplate
  extends AbstractQuoteTemplate<IQuoteFreightTemplateData>
  implements IQuoteFreightTemplate {
  constructor(data?: IQuoteFreightTemplateData) {
    super(data);
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class QuotePaymentTermTemplate
  extends AbstractQuoteTemplate<IQuotePaymentTermTemplateData>
  implements IQuotePaymentTermTemplate {
  is_archived = false;
  code: IQuotePaymentTermCode | number | null = null;
  constructor(data?: IQuotePaymentTermTemplateData) {
    super(data);
    if (data) {
      this.is_archived = data.is_archived ?? false;
      if (data.code) {
        if (typeof data.code == 'number') {
          this.code = data.code;
        } else {
          this.code = new QuotePaymentTermCode(data.code);
        }
      }
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

export class QuoteTermTemplate
  extends AbstractQuoteTemplate<IQuoteTermTemplateData>
  implements IQuoteTermTemplate {
  organization: number = QuoteOrganizationEnum.Jamesway;
  constructor(data?: IQuoteTermTemplateData) {
    super(data);
    if (data) {
      this.organization = data.organization;
    }
  }

  validate(): boolean {
    throw new Error('Method not implemented.');
  }
}

// Field Errors
// LH - 2023-09-11 - look into making this abstract and using the template types as subclasses
export class QuoteTemplateFieldError implements IQuoteTemplateFieldError {
  title: string[] = [];
  description: string[] = [];
  note: string[] = [];

  constructor(data?: IQuoteTemplateFieldErrorData) {
    if (data) {
      this.title = data.title ?? [];
      this.description = data.description ?? [];
      this.note = data.note ?? [];
    }
  }

  clearErrors(): void {
    this.title.splice(0, this.title.length);
    this.description.splice(0, this.description.length);
    this.note.splice(0, this.note.length);
  }

  static isQuoteTemplateFieldError(
    data: IQuoteTemplateFieldErrorData
  ): boolean {
    if (data.title && Array.isArray(data.title)) {
      return true;
    }
    if (data.description && Array.isArray(data.description)) {
      return true;
    }
    if (data.note && Array.isArray(data.note)) {
      return true;
    }
    return false;
  }
}

export class QuoteTermTemplateFieldError extends QuoteTemplateFieldError
  implements IQuoteTermTemplateFieldError {
  organization: string[] = [];

  constructor(data?: IQuoteTermTemplateFieldErrorData) {
    super(data);
    if (data) {
      this.organization = data.organization ?? [];
    }
  }

  clearErrors(): void {
    super.clearErrors();
    this.organization.splice(0, this.organization.length);
  }

  static isQuoteTermTemplateFieldError(
    data: IQuoteTermTemplateFieldErrorData
  ): boolean {
    if (QuoteTemplateFieldError.isQuoteTemplateFieldError(data)) {
      return true;
    }
    if (data.organization && Array.isArray(data.organization)) {
      return true;
    }
    return false;
  }
}

export class QuoteServiceTemplateFieldError
  implements IQuoteServiceTemplateFieldError {
  title: string[] = [];
  description: string[] = [];
  note: string[] = [];
  price_computations: IServiceComputationFieldError[] = [];
  days_computations: IServiceComputationFieldError[] = [];

  constructor(data?: IQuoteServiceTemplateFieldErrorData) {
    if (data) {
      this.title = data.title ?? [];
      this.description = data.description ?? [];
      this.note = data.note ?? [];
      this.price_computations =
        data.price_computations?.map(
          computation => new ServiceComputationFieldError(computation)
        ) ?? [];
      this.days_computations =
        data.days_computations?.map(
          computation => new ServiceComputationFieldError(computation)
        ) ?? [];
    }
  }

  clearErrors(): void {
    this.title.splice(0, this.title.length);
    this.description.splice(0, this.description.length);
    this.note.splice(0, this.note.length);
    this.price_computations.forEach(computation => {
      computation.clearErrors();
    });
    this.days_computations.forEach(computation => {
      computation.clearErrors();
    });
  }

  static isQuoteServiceTemplateFieldError(
    data: IQuoteServiceTemplateFieldErrorData
  ): boolean {
    if (data.title && Array.isArray(data.title)) {
      return true;
    }
    if (data.description && Array.isArray(data.description)) {
      return true;
    }
    if (data.note && Array.isArray(data.note)) {
      return true;
    }
    if (data.price_computations && Array.isArray(data.price_computations)) {
      return true;
    }
    if (data.days_computations && Array.isArray(data.days_computations)) {
      return true;
    }
    return false;
  }
}

export class QuotePaymentTermTemplateFieldError
  implements IQuotePaymentTermTemplateFieldError {
  title: string[] = [];
  description: string[] = [];
  note: string[] = [];
  is_archived: string[] = [];
  code: string[] = [];

  constructor(data?: IQuotePaymentTermTemplateFieldErrorData) {
    if (data) {
      this.title = data.title ?? [];
      this.description = data.description ?? [];
      this.note = data.note ?? [];
      this.is_archived = data.is_archived ?? [];
      this.code = data.code ?? [];
    }
  }

  clearErrors(): void {
    this.title.splice(0, this.title.length);
    this.description.splice(0, this.description.length);
    this.note.splice(0, this.note.length);
    this.is_archived.splice(0, this.is_archived.length);
    this.code.splice(0, this.code.length);
  }

  static isQuotePaymentTermTemplateFieldError(
    data: IQuotePaymentTermTemplateFieldErrorData
  ): boolean {
    if (data.title && Array.isArray(data.title)) {
      return true;
    }
    if (data.description && Array.isArray(data.description)) {
      return true;
    }
    if (data.note && Array.isArray(data.note)) {
      return true;
    }
    if (data.is_archived && Array.isArray(data.is_archived)) {
      return true;
    }
    if (data.code && Array.isArray(data.code)) {
      return true;
    }
    return false;
  }
}
